/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface EtaProps {
  id: string;
  asOfTime: Date;
  estimatedArrivalTime: Date;
  calculatedArrivalTime: Date;
  currentTripId: string;
  waypointId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class EtaRecord extends Record<EtaProps>({
  id: null as any,
  asOfTime: null as any,
  estimatedArrivalTime: null as any,
  calculatedArrivalTime: null as any,
  currentTripId: null as any,
  waypointId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    asOfTime: string | number | Date;
    estimatedArrivalTime: string | number | Date;
    calculatedArrivalTime: string | number | Date;
    currentTripId: string;
    waypointId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): EtaRecord {
    if (json == null) {
      throw new Error('EtaRecord.fromJS: parameter cannot be null');
    }
    return new EtaRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'EtaJson.id',
      ),
      asOfTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'asOfTime', '0001-01-01T00:00:00.00Z'),
          'EtaJson.asOfTime',
        ),
      ),
      estimatedArrivalTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'estimatedArrivalTime', '0001-01-01T00:00:00.00Z'),
          'EtaJson.estimatedArrivalTime',
        ),
      ),
      calculatedArrivalTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'calculatedArrivalTime', '0001-01-01T00:00:00.00Z'),
          'EtaJson.calculatedArrivalTime',
        ),
      ),
      currentTripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'currentTripId', '00000000-0000-0000-0000-000000000000'),
        'EtaJson.currentTripId',
      ),
      waypointId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'waypointId', '00000000-0000-0000-0000-000000000000'),
        'EtaJson.waypointId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'EtaJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    asOfTime: string | number | Date;
    estimatedArrivalTime: string | number | Date;
    calculatedArrivalTime: string | number | Date;
    currentTripId: string;
    waypointId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [EtaRecord, Error[]] {
    if (json == null) {
      throw new Error('EtaRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new EtaRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'EtaJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      asOfTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'asOfTime', '0001-01-01T00:00:00.00Z'),
              'EtaJson.asOfTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedArrivalTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'estimatedArrivalTime', '0001-01-01T00:00:00.00Z'),
              'EtaJson.estimatedArrivalTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      calculatedArrivalTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'calculatedArrivalTime', '0001-01-01T00:00:00.00Z'),
              'EtaJson.calculatedArrivalTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentTripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'currentTripId', '00000000-0000-0000-0000-000000000000'),
            'EtaJson.currentTripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      waypointId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'waypointId', '00000000-0000-0000-0000-000000000000'),
            'EtaJson.waypointId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'EtaJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: EtaProps) {
    super(props);
    if (props == null) {
      throw new Error('EtaRecord constructor parameter cannot be null');
    }
  }
}

export abstract class EtaViewModelBase implements EtaProps {
  protected readonly _props: EtaProps;
  private _json?: EtaProps;

  get id(): string {
    return this._props.id;
  }
  get asOfTime(): Date {
    return this._props.asOfTime;
  }
  get estimatedArrivalTime(): Date {
    return this._props.estimatedArrivalTime;
  }
  get calculatedArrivalTime(): Date {
    return this._props.calculatedArrivalTime;
  }
  get currentTripId(): string {
    return this._props.currentTripId;
  }
  get waypointId(): string {
    return this._props.waypointId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: EtaProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        asOfTime: this._props.asOfTime,
        estimatedArrivalTime: this._props.estimatedArrivalTime,
        calculatedArrivalTime: this._props.calculatedArrivalTime,
        currentTripId: this._props.currentTripId,
        waypointId: this._props.waypointId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllEtaEntities = (state: AppState) => state.entityStores.eta.entities;

export const selectEtaById = (id: string) => (state: AppState) =>
  state.entityStores.eta.entities.get(id);

export const selectEtaMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.eta.meta.get(id);

export const selectEtaAndMetadataById = (id: string) =>
  createSelector(selectEtaById(id), selectEtaMetadataById(id), (eta, meta) => ({ eta, meta }));

export function selectEtaEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllEtaEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, EtaRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectEtaEntitiesByAsOfTime(asOfTime: Date) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.asOfTime === asOfTime).valueSeq(),
  );
}

export function selectEtaEntitiesByEstimatedArrivalTime(estimatedArrivalTime: Date) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.estimatedArrivalTime === estimatedArrivalTime).valueSeq(),
  );
}

export function selectEtaEntitiesByCalculatedArrivalTime(calculatedArrivalTime: Date) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.calculatedArrivalTime === calculatedArrivalTime).valueSeq(),
  );
}

export function selectEtaEntitiesByCurrentTripId(currentTripId: string) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.currentTripId === currentTripId).valueSeq(),
  );
}

export function selectEtaEntitiesByWaypointId(waypointId: string) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.waypointId === waypointId).valueSeq(),
  );
}

export function selectEtaEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllEtaEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
